import { Link, useLocation } from "react-router-dom";
import type { NodeSummaryView } from "@k8s-dashboard/contracts";
import { drillKeys, useNodeList } from "@/api/queries";
import { Panel, StatusBadge } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { VirtualTable } from "@/components/VirtualTable";
import { UsageBar } from "@/components/drill";
import { useT } from "@/i18n/I18nProvider";
import { duration, num } from "@/lib/format";
import { useDashboardParams } from "@/state/useDashboardParams";
import { PageError, PageHeader, useDrillControls, useInvalidate } from "../drill/common";
import { nodeDetailPath } from "./nodePath";
import { ratioExceeds, ratioPercentText } from "./nodeDisplay";

/**
 * Nodes — 클러스터의 노드 목록·용량 대비 요청량입니다.
 *
 * 목록은 경량 요약(`?view=summary`)을 씁니다(#207). 노드별 Pod 행을 **목록에서
 * 빼고** 상세로 옮겼습니다 — 목록이 클러스터의 Pod 수에 비례해 커지면 노드가
 * 많은 클러스터에서 첫 화면이 가장 느립니다. Pod는 노드 상세에서 봅니다.
 *
 * 서버 상한이 500 노드이므로 **목록은 가상 스크롤**입니다. 서버가 개수를 자르는 것과
 * DOM 행 수를 묶는 것은 다른 문제입니다 — 500행을 한 번에 그리면 첫 페인트가 느립니다.
 *
 * 행의 신원은 이름이 아니라 **UID**입니다. 이름은 재사용되므로 이름을 key로 쓰면
 * 재생성된 노드가 이전 노드의 행 상태를 물려받습니다.
 *
 * 노드는 클러스터 스코프 리소스라 namespace 제한 사용자에게는 서버가 권한 없음
 * 섹션을 내려보냅니다.
 */
export function NodesView() {
  const { search } = useLocation();
  const { clusterId, refreshMs } = useDashboardParams();
  const q = useNodeList(clusterId, refreshMs);
  const invalidate = useInvalidate(drillKeys.nodes(clusterId));
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);

  const list = q.data?.nodes.data?.nodes ?? [];
  const truncation = q.data?.nodes.data?.truncation;
  const bytesTruncated = q.data?.nodes.data?.bytesTruncated ?? false;
  const readyCount = list.filter((n) => n.ready).length;

  return (
    <div className="page">
      <PageHeader
        title="Nodes"
        subtitle={list.length ? `${clusterId} · ${readyCount}/${list.length} Ready` : clusterId}
        controls={controls}
      />
      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <Panel
          title="노드 상태"
          subtitle="용량 대비 요청량은 스케줄된 Pod의 request 합계입니다"
          section={q.data?.nodes}
          referenceIso={q.data?.generatedAt}
          flush
        >
          <SectionView
            section={q.data?.nodes}
            loading={q.isLoading}
            emptyTitle="노드가 없습니다"
            emptyDetail="이 클러스터에서 조회 가능한 노드가 없습니다."
          >
            {() => (
              <>
                <NodeTable items={list} search={search} />
                <NodeListTruncation
                  omitted={truncation?.omitted ?? 0}
                  total={truncation?.total ?? 0}
                  bytesTruncated={bytesTruncated}
                />
              </>
            )}
          </SectionView>
        </Panel>
      )}
    </div>
  );
}

/** 잘린 사실은 숨기지 않습니다 — 운영자가 "전부 본 줄" 알면 안 됩니다. */
function NodeListTruncation({
  omitted,
  total,
  bytesTruncated,
}: {
  omitted: number;
  total: number;
  bytesTruncated: boolean;
}) {
  const { t } = useT("common");
  if (omitted <= 0) return null;
  return (
    <p className="muted" role="note" style={{ padding: "var(--space-4)", margin: 0 }}>
      {t("common.node.truncated", { omitted: num(omitted), total: num(total) })}
      {bytesTruncated ? ` ${t("common.node.truncatedBytes")}` : ""}
    </p>
  );
}

const COLS = ["auto", "110px", "110px", "210px", "210px", "130px", "90px"];

function NodeTable({ items, search }: { items: NodeSummaryView[]; search: string }) {
  const { t } = useT("common");
  return (
    <VirtualTable
      items={items}
      rowHeight={56}
      height={620}
      columns={COLS}
      // key는 UID입니다. 이름은 재사용되므로 신원이 될 수 없습니다.
      getKey={(n) => n.uid}
      header={
        <tr>
          <th>Node</th>
          <th style={{ width: COLS[1] }}>상태</th>
          <th className="ds-num" style={{ width: COLS[2] }}>
            Pods
          </th>
          <th style={{ width: COLS[3] }}>CPU / 할당가능</th>
          <th style={{ width: COLS[4] }}>Memory / 할당가능</th>
          <th style={{ width: COLS[5] }}>kubelet</th>
          <th className="ds-num" style={{ width: COLS[6] }}>
            나이
          </th>
        </tr>
      }
      renderRow={(n) => (
        <>
          <td className="ds-ident">
            <Link to={nodeDetailPath(n.name, n.uid, search)}>{n.name}</Link>
            <div className="muted" style={{ fontSize: 12 }}>
              {n.roles.length ? n.roles.join(" · ") : "역할 없음"}
              {n.internalIP ? ` · ${n.internalIP}` : ""}
              {n.unschedulable ? " · 스케줄 중지(cordon)" : ""}
            </div>
          </td>
          <td style={{ width: COLS[1] }}>
            <StatusBadge severity={n.severity} small />
          </td>
          <td className="ds-num" style={{ width: COLS[2] }}>
            {num(n.podsTotal)}/{num(n.allocatable.pods)}
          </td>
          <td style={{ width: COLS[3] }}>
            <RequestCell
              ratio={n.cpuRequestRatio}
              detail={`${num(n.requested.cpuMilli)}m / ${num(n.allocatable.cpuMilli)}m`}
              unavailable={t("common.node.ratio.unavailable")}
              over={(percent) => t("common.node.ratio.over", { percent })}
            />
          </td>
          <td style={{ width: COLS[4] }}>
            <RequestCell
              ratio={n.memoryRequestRatio}
              detail={`${num(n.requested.memoryMib)} Mi / ${num(n.allocatable.memoryMib)} Mi`}
              unavailable={t("common.node.ratio.unavailable")}
              over={(percent) => t("common.node.ratio.over", { percent })}
            />
          </td>
          <td className="muted" style={{ width: COLS[5] }}>
            {n.kubeletVersion}
          </td>
          <td className="ds-num" style={{ width: COLS[6] }}>
            {duration(n.ageSeconds)}
          </td>
        </>
      )}
    />
  );
}

/**
 * request/allocatable 셀.
 *
 * `max={2}`인 이유: 노드의 요청 합계가 allocatable을 **넘을 수 있습니다**(allocatable은
 * kubelet 보고에 따라 줄어들고, 이미 스케줄된 Pod는 남습니다). 1을 넘는 값을 깎으면
 * "꽉 찼다"와 "초과 할당됐다"가 같은 막대로 보입니다. 넘었을 때는 숫자도 함께 말합니다.
 */
function RequestCell({
  ratio,
  detail,
  unavailable,
  over,
}: {
  ratio: number | null;
  detail: string;
  unavailable: string;
  over: (percent: string) => string;
}) {
  const percent = ratioPercentText(ratio);
  return (
    <>
      <UsageBar ratio={ratio} label="Request" max={2} />
      <div className="muted" style={{ fontSize: 12 }}>
        {ratio === null ? unavailable : detail}
      </div>
      {ratioExceeds(ratio) && percent ? (
        <div className="muted" style={{ fontSize: 12 }}>
          {over(percent)}
        </div>
      ) : null}
    </>
  );
}
